import { CommunicationMergeTag, UnlayerMergeTags } from './communication-template.models';

/** Slug used as the key in Unlayer's nested merge-tag map. */
function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Folds the flat API catalogue into Unlayer's nested `mergeTags` shape so the editor renders grouped
 * sub-menus, carrying each tag's `sample` through for the in-canvas preview.
 *
 * Shared by the organisation-side template manager and the SB-15889 library curation screen: the
 * catalogue is global (the endpoint ignores its organisationId segment), so both editors offer the same
 * tags and there is no reason for two copies of the shaping.
 */
export function buildUnlayerMergeTags(tags: CommunicationMergeTag[]): UnlayerMergeTags {
  const grouped: UnlayerMergeTags = {};

  for (const tag of tags) {
    const groupKey = slug(tag.group) || 'general';
    const group = (grouped[groupKey] ??= { name: tag.group, mergeTags: {} });
    group.mergeTags![slug(tag.name)] = {
      name: tag.name,
      value: tag.value,
      ...(tag.sample ? { sample: tag.sample } : {})
    };
  }

  return grouped;
}

/** The Message text merge token, as the renderer matches it between `~~` delimiters. */
const MESSAGE_TEXT_TOKEN = '{MESSAGETEXT}';

/**
 * The catalogue a custom template is offered: every tag but Message text (SB-17215). A custom template is sent as
 * the body of the school's standard message, so the tag has no message to stand for; system templates keep it.
 */
export function withoutMessageText(tags: CommunicationMergeTag[]): CommunicationMergeTag[] {
  return tags.filter(tag => tag.value !== `~~${MESSAGE_TEXT_TOKEN}~~`);
}

/**
 * Whether any of the texts holds the Message text tag as the renderer resolves it: split on `~~`, a piece that is
 * exactly `{MESSAGETEXT}`. The server refuses a custom template holding it (SB-17215), and checks the same way.
 */
export function containsMessageTextTag(...texts: (string | null | undefined)[]): boolean {
  return texts.some(text => !!text && text.split('~~').includes(MESSAGE_TEXT_TOKEN));
}

/** The subject as it is stored: runs of whitespace collapsed to one space, and trimmed. */
export function normaliseSubject(subject: string | null | undefined): string {
  return (subject ?? '').replace(/\s+/g, ' ').trim();
}
